import { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { faPlain, toman } from 'utilities/helper/format'

/** `MoneypoolCashin.tag` is CharField(max_length=300). */
const DESCRIPTION_MAX_LENGTH = 300

/**
 * Confirming «ثبت پرداخت» for a basket of installments.
 *
 * The shared `AddJustPayForOtherModal` cannot be used here: it posts a
 * **حق عضویت** cash-in (`type: 'cnp'`) for `item.unpaid_portion`, so on a loan
 * it would have credited the member's dues instead of repaying the loan — and
 * only ever for one amount. This one records the installments, one call each.
 *
 * The amount shown is the installments only. A recorded payment is cash handed
 * to the manager: it carries no commission, so repeating the «هزینه اشتراک»
 * from the gateway receipt above would overstate what changes hands.
 *
 * The description belongs on THIS screen and not on the gateway path above it:
 * a recorded repayment is the manager asserting that cash arrived outside
 * Hamyan, and the note is how they say where it came from. A member paying
 * their own installment through a gateway has nothing to add that the receipt
 * does not already carry.
 */
export default function RecordPaymentModal({
    show,
    onHide,
    count,
    amount,
    isRecording,
    onConfirm,
}: {
    show: boolean
    onHide: () => void
    count: number
    amount: number
    isRecording: boolean
    onConfirm: (description: string) => void
}) {
    const [description, setDescription] = useState('')

    // Reopening the modal after a cancel must not resurrect the abandoned note.
    useEffect(() => {
        if (!show) setDescription('')
    }, [show])

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="sm"
            aria-labelledby="record-payment-title"
            centered
        >
            <div className="px-5 py-4 text-center">
                <p
                    id="record-payment-title"
                    className="text-base font-bold text-heading"
                >
                    ثبت پرداخت {faPlain.format(count)} قسط
                </p>
                <p className="mt-2 text-[15px] font-bold text-heading">
                    {toman(amount)}
                </p>
                <p className="mt-2 text-sm text-text-secondary leading-6">
                    تایید، به منزله‌ی دریافت این مبلغ از عضو و نگهداری آن نزد
                    مدیر صندوق است. برای ثبت دستی کارمزدی گرفته نمی‌شود.
                </p>

                <div className="mt-3 text-right">
                    <label
                        htmlFor="record-installment-description"
                        className="text-sm text-text-secondary"
                    >
                        توضیحات (اختیاری)
                    </label>
                    <textarea
                        id="record-installment-description"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        disabled={isRecording}
                        rows={2}
                        placeholder="مثلاً: نقدی تحویل گرفتم #تسویه_نقدی"
                        className="mt-1 w-full resize-none rounded-[8px] border border-stroke p-2 text-sm text-heading outline-none focus:border-green-1 focus:outline-none disabled:opacity-60"
                    />
                    <p className="mt-1 text-xs text-text-secondary leading-5">
                        {count > 1
                            ? 'این توضیح روی هر قسط ثبت می‌شود. '
                            : ''}
                        هر واژه‌ای که با # شروع شود هشتگ می‌شود و بعداً می‌توانید
                        تراکنش‌ها را با آن فیلتر کنید.
                    </p>
                </div>

                <button
                    type="button"
                    disabled={isRecording}
                    onClick={() => onConfirm(description)}
                    className="mt-4 w-full rounded-full bg-green-1 py-2.5 text-[15px] font-bold text-white active:scale-[0.99] transition-transform motion-reduce:transition-none disabled:opacity-60"
                >
                    {isRecording
                        ? 'در حال ثبت…'
                        : `تایید و ثبت ${faPlain.format(count)} قسط`}
                </button>
                <button
                    type="button"
                    disabled={isRecording}
                    onClick={onHide}
                    className="mt-1 w-full py-2 text-center text-sm text-text-secondary disabled:opacity-60"
                >
                    بازگشت
                </button>
            </div>
        </Modal>
    )
}
